/**
 * Match configuration (input to `@hf/sim` createInitialState) and match results.
 * Both peers of an online match must create their state from an IDENTICAL MatchConfig (the server sends it in
 * `match_found`).
 */
import type { CharacterId } from './characters';
import { DRIVES_PER_PLAYER } from './constants';
import type { PlayerIndex } from './enums';
import { MatchFormat, SpecialsMode } from './enums';

export interface MatchConfig {
  /** 32-bit seed for the sim PRNG. */
  seed: number;
  format: MatchFormat;
  /** Character for player 0 (HOME, red) and player 1 (AWAY, blue). */
  characters: readonly [CharacterId, CharacterId];
  /** Ranked normalization: all stats 3, identical hitboxes. */
  normalizedStats: boolean;
  specials: SpecialsMode;
  /** Drives format: possessions per player. */
  drivesPerPlayer: number;
  /** Who gets the ball first. */
  firstPossession: PlayerIndex;
  /**
   * Wind, yd/s² (signed; + pushes the ball toward +x for player 0's first possession). Server-chosen (from the
   * seed or settings), |wind| ≤ PHYSICS.maxWind, 0 allowed and used in ranked. Applied to the ball in flight only;
   * the sim flips it with the attack direction (rules.windX) so both sides get the same conditions.
   */
  wind: number;
  /**
   * Stadium time of day: cosmetic only (renderer stadium, menu chyrons), never read by the sim, so it is not part
   * of the sim state, its hash or SIM_CONTENT_HASH. Optional for compatibility: when omitted, every client derives
   * it from the seed (`timeOfDayOf`: `seed & 4` → night), the rule used before the field existed, so peers that
   * share a config always agree.
   */
  timeOfDay?: TimeOfDay;
}

/** Stadium lighting (VISION §7 "stadiums"): a day game or a night game under the floodlights. */
export type TimeOfDay = 'day' | 'night';
export const TIMES_OF_DAY: readonly TimeOfDay[] = ['day', 'night'];

/** The stadium time of day of a match: `config.timeOfDay` when set, else derived from the seed (`seed & 4`). */
export const timeOfDayOf = (
  config: Pick<MatchConfig, 'seed'> & { timeOfDay?: TimeOfDay | undefined },
): TimeOfDay =>
  config.timeOfDay === 'day' || config.timeOfDay === 'night'
    ? config.timeOfDay
    : (config.seed & 4) !== 0
      ? 'night'
      : 'day';

export const defaultMatchConfig = (overrides: Partial<MatchConfig> = {}): MatchConfig => ({
  seed: 1,
  format: MatchFormat.DRIVES,
  characters: ['rookie', 'bigtex'],
  normalizedStats: false,
  specials: SpecialsMode.SIGNATURE,
  drivesPerPlayer: DRIVES_PER_PLAYER,
  firstPossession: 0,
  wind: 0,
  ...overrides,
});

/**
 * Ranked preset: drives format, normalized stats, NO specials (VISION §5 ranked toggle = Off or the Standard
 * pool; STANDARD is reserved until the pool exists), no wind.
 */
export const rankedMatchConfig = (
  seed: number,
  characters: readonly [CharacterId, CharacterId],
  firstPossession: PlayerIndex,
): MatchConfig =>
  defaultMatchConfig({
    seed,
    characters,
    firstPossession,
    normalizedStats: true,
    format: MatchFormat.DRIVES,
    specials: SpecialsMode.OFF,
    wind: 0,
  });

/** Outcome of a finished match (sent by both clients in `result_report`; the server accepts on agreement). */
export interface MatchResult {
  /** Winning player index, or -1 for no result (abandoned). Drives format never draws (sudden-death OT). */
  winner: PlayerIndex | -1;
  score: readonly [number, number];
  /** Final sim tick. */
  ticks: number;
  /** hashState at the final tick. */
  finalHash: number;
}
